import { Icon, iconSizes } from "@hubrise/react-components"
import * as ReactTable from "@tanstack/react-table"
import * as React from "react"

import { RowData } from "../types"

import { HeaderCell, HeaderRow, HeaderWrapper, IconLink, Resizer, SortIcons, StyledDropdownMenu, Text } from "./styles"

interface HeaderProps<ItemType extends { id: number }> {
  headerGroups: ReactTable.HeaderGroup<RowData<ItemType>>[]
  tableRef: React.RefObject<HTMLDivElement>
}

interface SortableCellProps {
  title: string
  sortDirection: boolean | ReactTable.SortDirection
}

const SortableText: React.FC<SortableCellProps> = ({ title, sortDirection }) => {
  return (
    <Text>
      {title}
      <SortIcons>
        {sortDirection === "asc" && <span>▲</span>}
        {sortDirection === "desc" && <span>▼</span>}
      </SortIcons>
    </Text>
  )
}

const Header = <ItemType extends { id: number }>({ headerGroups, tableRef }: HeaderProps<ItemType>): JSX.Element => {
  return (
    <HeaderWrapper>
      {headerGroups.map((headerGroup, index) => (
        <HeaderRow position={index} key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const columnDef = header.column.columnDef
            const meta = columnDef.meta!
            const title = columnDef.header?.toString() ?? ""
            const stickyPosition =
              meta.stickyIndex !== undefined
                ? headerGroup.headers.slice(0, meta.stickyIndex).reduce((acc, header) => acc + header.getSize(), 0)
                : undefined

            return (
              <HeaderCell
                key={header.id}
                data-testid={`header:${header.id}`}
                stickyPosition={stickyPosition}
                isLastSticky={meta.isLastSticky}
                sortable={header.column.getCanSort()}
                center={meta.center}
                width={header.getSize()}
                onMouseDown={(event) => {
                  if (event.button === 0) {
                    header.column.getToggleSortingHandler()?.(event)
                  }
                }}
              >
                {meta.columnType.type === "variant_group" && <Icon code="layers" size={iconSizes._20} />}
                {header.column.getCanSort() ? (
                  <SortableText title={title} sortDirection={header.column.getIsSorted()} />
                ) : (
                  <Text>{title}</Text>
                )}

                {meta.actions != null && meta.actions.length > 0 && (
                  <StyledDropdownMenu
                    link={({ onClick }) => (
                      <IconLink onClick={onClick}>
                        <Icon code="more_vert" size={iconSizes._20} />
                      </IconLink>
                    )}
                    actions={meta.actions}
                    refForScrollDetection={tableRef}
                  />
                )}

                <Resizer
                  onMouseDown={(e) => {
                    e.stopPropagation()
                    header.getResizeHandler()(e)
                  }}
                  onTouchStart={header.getResizeHandler()}
                />
              </HeaderCell>
            )
          })}
        </HeaderRow>
      ))}
    </HeaderWrapper>
  )
}

export default Header
